import React from 'react';
import {
  FunnelIcon,
  EyeIcon,
  ChartPieIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * Dynamic legend that shows what's currently being visualized
 * Driven entirely by the visualization spec from the LLM
 */
export default function VisualizationLegend({
  visualizationSpec,
  matchingCount = 0,
  totalCount = 0,
  onClear
}) {
  if (!visualizationSpec) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-sm text-gray-500 text-center">
          <ChartPieIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>Enter a prompt to visualize voter data</p>
          <p className="text-xs mt-1">e.g., "Show Democrats vs Republicans"</p>
        </div>
      </div>
    );
  }

  const {
    summary,
    filters = [],
    color_field,
    color_scheme,
    cluster_color_field,
    cluster_color_scheme,
    highlight_matching,
    dim_non_matching,
    show_heatmap_layer,
    heatmap_field,
    heatmap_color,
    // Bubble visualization
    show_bubbles,
    bubble_size_field,
    bubble_min_radius,
    bubble_max_radius,
    // Hexbin visualization
    show_hexbin,
    hexbin_radius,
    hexbin_color_field,
    hexbin_color_scheme
  } = visualizationSpec;

  // Determine what color scheme to show in legend
  const legendColorField = cluster_color_field || color_field;
  const legendColorScheme = cluster_color_scheme || color_scheme || {};

  // Build legend entries from the color scheme
  const legendEntries = Object.entries(legendColorScheme)
    .filter(([key]) => key !== 'default')
    .map(([value, color]) => ({ value, color }));

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with summary */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <EyeIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-gray-700">Active Visualization</span>
            </div>
            <p className="text-sm text-gray-600">{summary}</p>
          </div>
          {onClear && (
            <button
              onClick={onClear}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Clear visualization"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filters applied */}
      {filters.length > 0 && (
        <div className="p-3 border-b border-gray-100 bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <FunnelIcon className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Filters Applied</span>
          </div>
          <div className="space-y-1">
            {filters.map((filter, idx) => (
              <div key={idx} className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded inline-block mr-1">
                {filter.field} {filter.operator} {String(filter.value)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Color legend */}
      {legendEntries.length > 0 && (
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <ChartPieIcon className="h-4 w-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-600">
              Colored by: {legendColorField}
            </span>
          </div>
          <div className="space-y-1.5">
            {legendEntries.map(({ value, color }) => (
              <div key={value} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full border border-gray-200"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm text-gray-600">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Match stats */}
      {(highlight_matching || dim_non_matching || filters.length > 0) && matchingCount > 0 && (
        <div className="p-3 border-b border-gray-100 bg-green-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-green-700 font-medium">Matching Voters</span>
            <span className="text-sm text-green-800 font-bold">
              {matchingCount.toLocaleString()} / {totalCount.toLocaleString()}
            </span>
          </div>
          <div className="mt-2 bg-green-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-green-500 h-full transition-all duration-300"
              style={{ width: `${(matchingCount / totalCount) * 100}%` }}
            />
          </div>
          <div className="text-xs text-green-600 mt-1">
            {((matchingCount / totalCount) * 100).toFixed(1)}% of total
          </div>
        </div>
      )}

      {/* Heatmap indicator */}
      {show_heatmap_layer && (
        <div className="p-3 border-b border-gray-100 bg-orange-50">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-4 h-4 rounded"
              style={{
                background: `linear-gradient(to right, transparent, ${heatmap_color || '#3B82F6'})`
              }}
            />
            <span className="text-xs font-medium text-orange-700">Heatmap Layer</span>
          </div>
          <div className="text-xs text-orange-600">
            {heatmap_field
              ? `Intensity weighted by: ${heatmap_field}`
              : 'Showing voter density'}
          </div>
        </div>
      )}

      {/* Bubble visualization indicator */}
      {show_bubbles && bubble_size_field && (
        <div className="p-3 border-b border-gray-100 bg-purple-50">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-purple-400" />
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <div className="w-4 h-4 rounded-full bg-purple-600" />
            </div>
            <span className="text-xs font-medium text-purple-700">Bubble Layer</span>
          </div>
          <div className="text-xs text-purple-600">
            Size scaled by: {bubble_size_field}
          </div>
          <div className="text-xs text-purple-500 mt-1">
            Radius range: {bubble_min_radius || 8}px - {bubble_max_radius || 40}px
          </div>
        </div>
      )}

      {/* Hexbin visualization indicator */}
      {show_hexbin && (
        <div className="p-3 border-b border-gray-100 bg-teal-50">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-teal-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L3 7v10l9 5 9-5V7l-9-5zm0 2.18l6.63 3.68L12 11.54 5.37 7.86 12 4.18z"/>
            </svg>
            <span className="text-xs font-medium text-teal-700">Hexbin Aggregation</span>
          </div>
          <div className="text-xs text-teal-600">
            Hexagon size: {hexbin_radius || 50}px
          </div>
          {hexbin_color_field && (
            <div className="text-xs text-teal-600 mt-1">
              Colored by: {hexbin_color_field}
            </div>
          )}
          {hexbin_color_scheme && Object.keys(hexbin_color_scheme).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {Object.entries(hexbin_color_scheme)
                .filter(([key]) => key !== 'default')
                .map(([value, color]) => (
                  <div key={value} className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded border border-gray-200"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-teal-600">{value}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Cluster indicator */}
      {cluster_color_field && (
        <div className="p-3 bg-gray-50 rounded-b-lg">
          <div className="text-xs text-gray-500">
            <span className="font-medium">Cluster view:</span> Pie charts show {cluster_color_field} distribution
          </div>
          {filters.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-green-500 mr-1 align-middle" />
              Green ring = % matching filter
            </div>
          )}
        </div>
      )}
    </div>
  );
}
